import { DateTime } from "luxon";
import { declineReasonLabel } from "shared/app/land-x/decline-reasons";
import { Notification, RentalVerificationCardState } from "../types";

export type RentalVerificationFilter = "all" | "waiting" | "closed";

const rentalVerificationState = (
  item: Notification
): RentalVerificationCardState => item.rental_verification?.state ?? "closed";

export const isWaitingOnPartner = (item: Notification): boolean =>
  ["new", "started"].includes(rentalVerificationState(item));

export const matchesRentalVerificationFilter = (
  item: Notification,
  filter: RentalVerificationFilter
): boolean => {
  if (filter === "all") return true;
  const waiting = isWaitingOnPartner(item);
  return filter === "waiting" ? waiting : !waiting;
};

const formatTenancyRange = (
  moveIn?: string | null,
  moveOut?: string | null
): string | null => {
  const start = moveIn ? DateTime.fromISO(moveIn) : null;
  if (!start?.isValid) return null;
  const end = moveOut ? DateTime.fromISO(moveOut) : null;
  const endLabel = end?.isValid ? end.toFormat("MMM yyyy") : "Present";
  return `${start.toFormat("MMM yyyy")} – ${endLabel}`;
};

export const rentalVerificationLocation = (item: Notification): string => {
  const { property_name, unit, rental_address } = item.metadata;
  const property = property_name?.trim();
  const unitLabel = unit?.trim();
  if (property) return unitLabel ? `${property} · ${unitLabel}` : property;
  return rental_address?.trim() || "";
};

export type RentalVerificationBadgeTone =
  | "warning"
  | "success"
  | "error"
  | "base";

type RentalVerificationCardCopy = {
  label: string;
  badge?: { text: string; tone: RentalVerificationBadgeTone };
  // The rental period line, with the ask while the survey is open.
  line: string;
  // Who started, answered or declined it, or why it was closed.
  detail?: string;
  button?: "Answer survey" | "Continue survey";
};

export const rentalVerificationCardCopy = (
  item: Notification
): RentalVerificationCardCopy => {
  const range = formatTenancyRange(
    item.metadata.move_in_date,
    item.metadata.move_out_date
  );
  const listedYourUnit = `Listed your unit as a past rental${range ? ", " + range : ""}.`;
  const confirmLine = `${listedYourUnit} Confirm their rental history.`;

  switch (rentalVerificationState(item)) {
    case "new":
      return {
        label: "Rental verification requested",
        line: confirmLine,
        button: "Answer survey"
      };
    case "started": {
      const by = item.rental_verification?.started_by_name?.trim();
      return {
        label: "Rental verification requested",
        badge: { text: "In progress", tone: "warning" },
        line: confirmLine,
        detail: by ? `${by} started this survey` : undefined,
        button: "Continue survey"
      };
    }
    case "completed": {
      const by = item.rental_verification?.submitted_by_name?.trim();
      return {
        label: "Rental verification answered",
        badge: { text: "Completed", tone: "success" },
        line: listedYourUnit,
        detail: by ? `Answered by ${by}` : "Answered"
      };
    }
    case "declined": {
      const by = item.rental_verification?.submitted_by_name?.trim();
      const reason = declineReasonLabel(
        item.rental_verification?.decline_reason_code,
        item.rental_verification?.decline_reason
      );
      const declined = by ? `Declined by ${by}` : "Declined";
      return {
        label: "Rental verification declined",
        badge: { text: "Declined", tone: "error" },
        line: listedYourUnit,
        detail: reason ? `${declined}: ${reason}` : declined
      };
    }
    default:
      return {
        label: "Rental verification closed",
        badge: { text: "Closed", tone: "base" },
        line: listedYourUnit,
        detail: "This request is no longer needed"
      };
  }
};
